import { jsx as _jsx } from "react/jsx-runtime";
import { reportErrorToBugsnag } from "@air/utils-error";
import { useRouter } from "next/router";
import { createContext, memo, useCallback, useContext, useMemo, useRef } from "react";
import isEqual from "react-fast-compare";
import { useAccountContext } from "~/providers/AccountProvider";
import { FiltersModifiedManuallyProvider } from "~/providers/FiltersModifiedManuallyProvider";
import { isDevOrTestStage } from "~/swr-hooks/utils";
import { urlQueryToFilterParams } from "~/utils/filters/urlQueryToFilterParams";
const defaultValue = {
    filters: {},
    immutableFilters: {},
    accountId: undefined,
    search: undefined
};
const FiltersContext = /*#__PURE__*/ createContext(defaultValue);
export const FiltersProvider = /*#__PURE__*/ memo((param)=>{
    let { children, getImmutableFilters } = param;
    const { data: account } = useAccountContext();
    const { asPath, isReady } = useRouter();
    const filtersValue = useRef({
        ...defaultValue
    });
    // Stabilize getImmutableFilters to prevent unnecessary recalculations
    const stableGetImmutableFilters = useCallback((params)=>{
        var _getImmutableFilters;
        return (_getImmutableFilters = getImmutableFilters === null || getImmutableFilters === void 0 ? void 0 : getImmutableFilters(params)) !== null && _getImmutableFilters !== void 0 ? _getImmutableFilters : {};
    }, [
        getImmutableFilters
    ]);
    const contextValue = useMemo(()=>{
        // Don't process filters until router is ready (proper SSR handling)
        if (!isReady) {
            return {
                ...defaultValue,
                accountId: account === null || account === void 0 ? void 0 : account.id
            };
        }
        /**
     * Extract search params from asPath - this is SSR-safe and avoids unnecessary re-renders
     */ const searchParams = asPath.includes("?") ? asPath.split("?")[1] : "";
        const query = new URLSearchParams(searchParams);
        const filters = urlQueryToFilterParams({
            query
        });
        const immutableFilters = stableGetImmutableFilters({
            asPath,
            accountId: account === null || account === void 0 ? void 0 : account.id,
            userFilters: filters
        });
        const newFilters = {
            ...filters,
            immutableFilters,
            accountId: account === null || account === void 0 ? void 0 : account.id
        };
        if (isEqual(filtersValue.current, newFilters)) {
            return filtersValue.current;
        } else {
            filtersValue.current = newFilters;
            return newFilters;
        }
    }, [
        account === null || account === void 0 ? void 0 : account.id,
        asPath,
        isReady,
        stableGetImmutableFilters
    ]);
    return /*#__PURE__*/ _jsx(FiltersContext.Provider, {
        value: contextValue,
        children: /*#__PURE__*/ _jsx(FiltersModifiedManuallyProvider, {
            children: children
        })
    });
});
FiltersProvider.displayName = "FiltersProvider";
export const useFiltersContext = ()=>{
    const context = useContext(FiltersContext);
    if (context === defaultValue) {
        const error = "FiltersContext used outside of FiltersProvider";
        if (isDevOrTestStage()) {
            throw error;
        } else {
            reportErrorToBugsnag({
                error,
                context: error
            });
        }
    }
    return context;
};
